/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import {
	Settings,
	Platform,
	ShoppingItem,
	SofaRangeName,
	YmalSectionType,
} from 'sofa-reborn-react';
import { CookieConsentService } from '@inter-ikea-kompis/services';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { localeService, UrlService } from '../services';
import {
	InsightsApi,
	DesignInteractionEnum,
	IpexMomentEnum,
	IItems,
	IArticleData,
	DesignSourceEnum,
	PlatformEnum,
} from '@insights/insights-data-provider';
import { IShoppingProduct } from '@inter-ikea-kompis/types';
import { AnalyticsManager } from '@inter-ikea-kompis/analytics';

let insightDataProviderDisabled = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let insightsApi: any = null;

class StatisticsService {
	private static async getCookieConsent(settings: Settings): Promise<boolean> {
		const cookieConsentService = new CookieConsentService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const consentLevel = 2;
		try {
			return await cookieConsentService.hasCookieConsent(consentLevel);
		} catch {
			return false;
		}
	}

	private static async trackingEnabled(settings: Settings, platform: Platform): Promise<boolean> {
		if (platform === 'kiosk' || !ApplicationUtils.isProduction()) {
			return true;
		}

		try {
			return await StatisticsService.getCookieConsent(settings);
		} catch (_) {
			return false;
		}
	}

	public static async sendInitPlanningSessionInsightEvent(
		code: string,
		vpcSource: DesignSourceEnum,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.initPlanningSession(code, vpcSource);
		}
	}

	public static async saveDesignInsightEvent(
		vpcCode: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		twoSeparateSofas: boolean,
		orderItems: string[] | null
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.saveDesign(vpcCode, { isSeparateSofa: twoSeparateSofas, orderItems: orderItems });
		}
	}

	public static async sendVpcInteractionEvent(
		vpcCode: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.sendDesignInteractionEvent(vpcCode, DesignInteractionEnum.storeConfiguration);
		}
	}

	public static async sendAddToBagInsightEvent(
		bagType: string,
		products: IShoppingProduct[],
		shoppingItems: ShoppingItem[],
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		fromMain: boolean,
		vpcCode: string | null,
		productType?: string | null,
		sectionType?: YmalSectionType | null,
		isIkeaPlus?: boolean,
		isCoverOnly?: boolean,
		hasAdditionalCover?: boolean,
		containsSeparateSofa?: boolean,
		orderItems?: string[] | null
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}
		const content = products[0].product.content;
		if (content) {
			const currency: string = content.priceInformation?.salesPrice[0].currencyCode || '';
			const items = shoppingItems.map((shoppingItem: ShoppingItem) => {
				const ikeaProduct = products.find((product: IShoppingProduct) => {
					return (
						product.product.content &&
						(product.product.content.itemNoGlobal === shoppingItem.id ||
							product.product.content.itemNoLocal === shoppingItem.id)
					);
				});

				const item: IArticleData = {
					id: shoppingItem.id,
					price:
						ikeaProduct &&
						ikeaProduct.product.content &&
						ikeaProduct.product.content.priceInformation
							? ikeaProduct.product.content.priceInformation.salesPrice[0].priceInclTax *
							  shoppingItem.quantity
							: 0,
					qty: shoppingItem.quantity,
				};

				return item;
			});
			const payload = fromMain
				? {
						from: 'main',
						productType: productType,
						isIkeaPlus: isIkeaPlus,
						isCoverOnly: isCoverOnly,
						hasAdditionalCover: hasAdditionalCover,
						isSeparateSofa: containsSeparateSofa,
						orderItems: orderItems ? orderItems : null,
				  }
				: { from: 'ymal', sectionType: sectionType };
			const addToBagObject: IItems = {
				currency: currency,
				items: items,
				designId: vpcCode || undefined,
				payload: payload,
			};

			if (insightsApi) {
				if (bagType === 'Cart') {
					insightsApi.addToCart(addToBagObject);
				} else if (bagType === 'List') {
					insightsApi.addToWishList(addToBagObject);
				}
			}
		}
	}

	public static async sendCustomInsightEvent(
		event: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		moment: IpexMomentEnum,
		payload?: {}
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.sendEvent({
				ipexMoment: moment,
				event: event,
				payload: payload,
			});
		}
	}

	public static async initStatistics(
		settings: Settings,
		platform: Platform,
		range: SofaRangeName
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		insightsApi = new InsightsApi();
		AnalyticsManager.setInsightsApi(insightsApi);
		// No consent, do not send data to analytics
		if (trackingEnabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
		} else {
			StatisticsService.disableTracking();
		}
	}

	private static async initInsightDataProvider(
		platform: Platform,
		rangeName: SofaRangeName
	): Promise<void> {
		// Insight analytics data provider
		let storeId = UrlService.getStoreId();
		if (storeId) {
			const firstChar = storeId.match('[a-zA-Z]');
			// Check if storeId contains a char, if it does only get the numbers before the char
			if (firstChar) {
				const index = storeId.indexOf(firstChar[0]);
				storeId = storeId.substring(0, index);
			}
		}
		if (insightsApi) {
			const kioskId =
				UrlService.getApplicationParams().kioskId ||
				UrlService.getApplicationParams().computerId ||
				undefined;
			await insightsApi.connectApplication({
				applicationId: rangeName.toUpperCase(),
				applicationVersion: '1.5',
				platform: PlatformEnum.owfe,
				countryCode: localeService.getCountryCode(),
				languageCode: localeService.getLanguageCode(),
				storeId: platform === 'kiosk' ? storeId : null,
				kioskId: kioskId,
				kiosk: platform === 'kiosk',
				dev: !ApplicationUtils.isProduction(),
				isPlanner: true,
				enableGa: true,
			});
		}
	}

	public static setABVersion(abVersionName: string, abVersionVariation: string): void {
		if (insightsApi) {
			insightsApi.setAbVersion(abVersionName, abVersionVariation);
		}
	}

	private static disableTracking(): void {
		if (insightsApi) {
			insightsApi.disconnect();
		}
		insightDataProviderDisabled = true;
	}
}

export default StatisticsService;
